import { useState, useEffect, useRef } from 'react';
import {
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Globe,
  ShieldAlert,
  ShieldCheck,
  Activity,
} from 'lucide-react';
import { QuestionResponse, useQuestions } from '../../../hooks/useQuestions';

interface PestelChecklistProps {
  entityType: string;
  entityKey: string;
  companyId: string;
  title: string;
  className?: string;
  onScoreChange?: (score: number) => void;
}

export default function PestelChecklist({
  entityType,
  entityKey,
  companyId,
  title,
  className = '',
  onScoreChange,
}: PestelChecklistProps) {
  const { questions, loading, error, saveResponse, getQuestionResponse } = useQuestions(
    entityType,
    companyId,
    entityKey
  );

  // Local state to track optimistic updates for immediate UI feedback
  const [optimisticResponses, setOptimisticResponses] = useState<
    Record<string, 'opportunity' | 'threat' | null>
  >({});
  const [currentScore, setCurrentScore] = useState<number>(0);

  // Use ref to store the latest onScoreChange callback to avoid useEffect dependency issues
  const onScoreChangeRef = useRef(onScoreChange);
  onScoreChangeRef.current = onScoreChange;

  const handleQuestionResponse = async (
    questionId: string,
    value: 'opportunity' | 'threat' | null
  ) => {
    // Optimistically update the UI immediately
    setOptimisticResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    const response: QuestionResponse = {
      questionId,
      entityType,
      entityKey,
      response: value === null ? { selectedOptions: [] } : { selectedOptions: [value] },
    };

    const success = await saveResponse(response);
    if (!success) {
      console.error('Failed to save question response');
      // Revert optimistic update on failure
      setOptimisticResponses((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    } else {
      // Clear optimistic state once saved successfully (backend state will take over)
      setOptimisticResponses((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    }
  };

  // Function to get challenge icon and styling
  const getChallengeDisplay = (score: number, hasAnswered: boolean) => {
    if (!hasAnswered) {
      return {
        icon: Globe,
        className: 'text-gray-500',
        bgClassName: 'bg-gray-100',
        label: 'Not Analyzed',
      };
    }

    // Reverse logic: High opportunities = Low challenge
    if (score >= 80) {
      return {
        icon: ShieldCheck,
        className: 'text-blue-700',
        bgClassName: 'bg-blue-100',
        label: 'Very Low',
      };
    }

    if (score >= 60) {
      return {
        icon: ShieldCheck,
        className: 'text-green-700',
        bgClassName: 'bg-green-100',
        label: 'Low',
      };
    }

    if (score >= 40) {
      return {
        icon: Activity,
        className: 'text-yellow-700',
        bgClassName: 'bg-yellow-100',
        label: 'Moderate',
      };
    }

    if (score >= 20) {
      return {
        icon: ShieldAlert,
        className: 'text-orange-700',
        bgClassName: 'bg-orange-100',
        label: 'High',
      };
    }

    return {
      icon: ShieldAlert,
      className: 'text-red-700',
      bgClassName: 'bg-red-100',
      label: 'Very High',
    };
  };

  const getQuestionValue = (questionId: string): 'opportunity' | 'threat' | null => {
    // Check optimistic state first, then fall back to backend state
    if (optimisticResponses.hasOwnProperty(questionId)) {
      return optimisticResponses[questionId];
    }

    const response = getQuestionResponse(questionId, entityKey);
    const selectedOptions = response?.response?.selectedOptions;
    if (selectedOptions && selectedOptions.length > 0) {
      return selectedOptions[0] as 'opportunity' | 'threat';
    }
    return null;
  };

  const calculateScore = (): { score: number; hasAnswered: boolean } => {
    let opportunityCount = 0;
    let _threatCount = 0;
    let totalAnswered = 0;

    questions.forEach((question) => {
      const value = getQuestionValue(question.id);
      if (value) {
        totalAnswered++;
        if (value === 'opportunity') {
          opportunityCount++;
        } else if (value === 'threat') {
          _threatCount++;
        }
      }
    });

    // If nothing is answered, return 50 (neutral) but mark as not answered
    if (totalAnswered === 0) return { score: 50, hasAnswered: false };

    // Calculate score based on opportunities vs threats ratio
    // 100% = all opportunities, 0% = all threats, 50% = balanced
    const opportunityRatio = opportunityCount / totalAnswered;
    return { score: Math.round(opportunityRatio * 100), hasAnswered: totalAnswered > 0 };
  };

  // Update current score whenever questions, responses, or optimistic responses change
  useEffect(() => {
    if (questions.length > 0 && !loading) {
      const { score, hasAnswered } = calculateScore();

      // Set the display score (-1 for not analyzed, actual score if analyzed)
      const displayScore = hasAnswered ? score : -1;
      setCurrentScore(displayScore);

      // Report score change immediately
      if (onScoreChangeRef.current) {
        onScoreChangeRef.current(displayScore);
      }
    } else if (questions.length === 0 && !loading) {
      setCurrentScore(-1); // Show not analyzed when no questions

      // Report no analysis done
      if (onScoreChangeRef.current) {
        onScoreChangeRef.current(-1);
      }
    }
  }, [questions, optimisticResponses, loading]); // Removed onScoreChange from dependencies

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 h-full ${className}`}>
        <div className="p-6">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 h-full ${className}`}>
        <div className="p-6">
          <div className="flex items-center justify-center h-32 text-center">
            <div>
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 h-full ${className}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <CheckCircle2 className="h-5 w-5 mr-2 text-blue-600" />
            {title}
          </h3>
          {(() => {
            const { hasAnswered } = calculateScore();
            const challengeDisplay = getChallengeDisplay(currentScore, hasAnswered);
            const IconComponent = challengeDisplay.icon;
            return (
              <div
                className={`flex items-center space-x-2 px-3 py-1 rounded-lg ${challengeDisplay.bgClassName}`}
              >
                <IconComponent className={`h-4 w-4 ${challengeDisplay.className}`} />
                <span className={`text-sm font-medium ${challengeDisplay.className}`}>
                  {challengeDisplay.label}
                </span>
              </div>
            );
          })()}
        </div>

        {questions.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No questions available</p>
          </div>
        ) : (
          <div className="space-y-4 overflow-y-auto max-h-[calc(100%-100px)]">
            {questions.map((question) => {
              const currentValue = getQuestionValue(question.id);

              return (
                <div
                  key={question.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="mb-3">
                    <p className="text-sm text-gray-800 font-medium leading-relaxed">
                      {question.content}
                    </p>
                  </div>

                  <div className="flex items-center space-x-4">
                    {/* Opportunity Radio Button */}
                    <label className="flex items-center cursor-pointer group">
                      <div className="relative">
                        <input
                          type="radio"
                          name={`question-${question.id}`}
                          checked={currentValue === 'opportunity'}
                          onChange={() => handleQuestionResponse(question.id, 'opportunity')}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                            currentValue === 'opportunity'
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-300 group-hover:border-green-400'
                          }`}
                        >
                          {currentValue === 'opportunity' && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                      </div>
                      <div className="ml-3 flex items-center">
                        <TrendingUp className="h-4 w-4 text-green-600 mr-1.5" />
                        <span
                          className={`text-sm font-medium transition-colors ${
                            currentValue === 'opportunity'
                              ? 'text-green-700'
                              : 'text-gray-600 group-hover:text-green-600'
                          }`}
                        >
                          Opportunity
                        </span>
                      </div>
                    </label>

                    {/* Threat Radio Button */}
                    <label className="flex items-center cursor-pointer group">
                      <div className="relative">
                        <input
                          type="radio"
                          name={`question-${question.id}`}
                          checked={currentValue === 'threat'}
                          onChange={() => handleQuestionResponse(question.id, 'threat')}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                            currentValue === 'threat'
                              ? 'border-red-500 bg-red-500'
                              : 'border-gray-300 group-hover:border-red-400'
                          }`}
                        >
                          {currentValue === 'threat' && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                      </div>
                      <div className="ml-3 flex items-center">
                        <TrendingDown className="h-4 w-4 text-red-600 mr-1.5" />
                        <span
                          className={`text-sm font-medium transition-colors ${
                            currentValue === 'threat'
                              ? 'text-red-700'
                              : 'text-gray-600 group-hover:text-red-600'
                          }`}
                        >
                          Threat
                        </span>
                      </div>
                    </label>

                    {/* Clear Selection Button */}
                    {currentValue && (
                      <button
                        onClick={() => handleQuestionResponse(question.id, null)}
                        className="text-xs text-gray-400 hover:text-gray-600 ml-2"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
